import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import ModalConfirm from "components/Common/ModalConfirm";
import { useRole } from "components/Hooks/UserHooks";
import CountUp from "react-countup";
import ModalPreview from "components/Common/ModalPreview";
import { Link } from "react-router-dom";
import TooltipCustom from "components/Common/TooltipCustom";
import { formatDateStringToKorea } from "helpers/format";
import TableGridContainer from "components/Common/TableGridContainer";
import FeatherIcon from "feather-icons-react";
import StickerForm from "./StickerForm";
import { getStickers, putSticker } from "store/sticker/thunk";
import stickerApi from "api/stickerApi";
import LabelWrapper from "components/Common/LabelWrapper";
import CollapseFilter from "components/Common/CollapseFilter";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import { COLOR_STICKER_STATUS, LIST_OPTION_BAR_DETAIL_REVIEW_STATUS } from "helpers/constans";

export interface Tag {
  id: string;
  text: string;
}

registerLocale("en", en);
registerLocale("ko", ko);

export interface ISearch {
  page: number;
  limit: number;
  keyword: string;
}

const DEFAULT_DATA_SEARCH = {
  page: 1,
  limit: 30,
  keyword: "",
};

export interface IProps {
  stickerPackageId?: string
}

const StickerList = ({
  stickerPackageId,
}: IProps) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [querySticker, setQuerySticker] = useState<ISearch | any>(DEFAULT_DATA_SEARCH)
  const [typeShowMode, setTypeShowMode] = useState<string>("TABLE");

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemToEdit, setItemToEdit] = useState<any>(false);
  const [linkImage, setLinkImage] = useState<any>("");
  const [lstSticker, setLstSticker] = useState<any>([]);

  const [keywordSearch, setKeywordSearch] = useState<string>("");
  const [statusSearch, setStatusSearch] = useState<string>("");

  const loadTableData = async (queryData?: ISearch) => {
    if (!stickerPackageId) return;

    setIsLoading(true);
    try {
      const response: any = await getStickers({
        ...queryData,
        stickerPackageId: stickerPackageId
      });

      if (response?.data) {
        setLstSticker(response?.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error)
    }
  }

  const handleOnConfirmUpdate = async (item: {
    id: number | string;
    status: boolean;
  }) => {
    try {
      if (!item) {
        return;
      }
      const response: any = await putSticker(
        item?.id,
        { status: item?.status === false ? 0 : 1 }
      );
      if (response?.code === 200) {
        loadTableData(querySticker);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  // Begin::Update User
  const handleClickEdit = (item: any) => {
    setIsOpen(true);
    setItemToEdit(item);
  };

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };
  //End::Update User

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div>
              {lstSticker?.meta?.total -
                ((querySticker?.page - 1) * querySticker?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        thWidth: 80,
        filterable: false,
        sortable: false,
        thClass: "text-center",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                id="customSwitchsizelg"
                checked={item?.status === 1}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    id: item?.id,
                    status: !!value ? true : false,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Image"),
        accessor: "url",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        thWidth: 150,
        Cell: (cell: any) => {
          const imgUrl = cell?.value;

          return imgUrl ? (
            <>
              <div className="w-100 text-center " style={{ minWidth: "150px" }}>
                <div
                  style={{ width: "90px", height: "90px" }}
                  className="m-auto cursor-pointer"
                  onClick={() => {
                    setLinkImage(imgUrl);
                    document.body.className = "modal-open_img";
                  }}
                >
                  <img
                    src={imgUrl}
                    className="flex-shrink-0 rounded w-100"
                    alt="thumbnail"
                    width={90}
                    height={90}
                  />
                </div>
              </div>
            </>
          ) : (
            ""
          );
        },
      },
      {
        Header: t("Name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            cell?.value && (
              <>
                <div
                  className="cursor-pointer"
                >
                  {cell?.value}
                </div>
              </>
            )
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "createdAt",
        filterable: true,
        sortable: true,
        thWidth: 150,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end pe-3" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 150,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ justifyContent: "center" }}
            >
              <TooltipCustom
                title={t("Button Update")}
                id={`update-md-${item?.id}`}
              >
                <li className="list-inline-item" id={`update-md-${item?.id}`}>
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClickEdit(item);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom "></i>
                  </Link>
                </li>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete")}
                id={`delete-md-${item?.id}`}
              >
                <li className="list-inline-item" id={`delete-md-${item?.id}`}>
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setItemToDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </li>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, lstSticker?.meta?.total]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuerySticker({ ...querySticker, page: page + 1 });
    loadTableData({ ...querySticker, page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseImageClick = () => {
    setLinkImage((_prev: any) => "");
  };
  // Begin::Delete

  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await stickerApi.deleteSticker(id);

      if (response?.data) {
        loadTableData(DEFAULT_DATA_SEARCH);
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const triggerRefresh = () => {
    setItemToEdit(null);
    onCloseClick();
    loadTableData(DEFAULT_DATA_SEARCH);
  };

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuerySticker((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeViewTable = () => {
    if (typeShowMode === "CARD") {
      setTypeShowMode("TABLE");
    } else setTypeShowMode("CARD");
  };

  const handleChangeKeyword = (key: string, value: string) => {
    setQuerySticker({
      ...querySticker,
      [key]: value
    })
  }

  const searchData = () => {
    loadTableData(querySticker);
  };

  const resetData = () => {
    setQuerySticker(DEFAULT_DATA_SEARCH);
    setKeywordSearch((_prev) => "");
    setStatusSearch("");
    loadTableData(DEFAULT_DATA_SEARCH);
  };

  useEffect(() => {
    if (stickerPackageId) {
      loadTableData(DEFAULT_DATA_SEARCH)
    }
  }, [])

  // End::Delete
  useEffect(() => {
    document.title = `${t("Sticker Management")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="mt-3">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <div className="d-block d-xl-flex justify-content-between align-items-start">
                    <div
                      className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                      style={{ maxWidth: "100%" }}
                    >
                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Sticker")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={lstSticker?.meta?.summary?.totalSticker || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>

                    <div className="col-sm-auto">
                      <button
                        type="button"
                        className="btn btn-success d-none d-md-block"
                        onClick={() => {
                          setIsOpen(true)
                          setItemToEdit(null)
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Button Create Sticker")}
                      </button>
                    </div>
                  </div>
                </CardHeader>

                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={t("Keyword")}
                          isShow={!!keywordSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Keyword")}...`}
                            value={keywordSearch}
                            onChange={(e) => {
                              setKeywordSearch(e?.target?.value);
                              handleChangeKeyword("keyword", e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Status")} isShow={true}>
                          <DropdownStatusNew
                            name="status"
                            dataList={LIST_OPTION_BAR_DETAIL_REVIEW_STATUS || []}
                            placeholder={`${t("Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) => {
                              handleChangeKeyword("status", e?.value);
                              setStatusSearch(e?.value);
                            }}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_STICKER_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={6}
                        xl={6}
                        xxl={6}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>

                <Row className="g-4 align-items-center mt-0 mt-md-2">
                  <Col
                    sm={12}
                    md={12}
                    lg={12}
                    className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-1 mt-md-0"
                  >
                    <div className="ms-2 mx-3 mb-2">
                      <button
                        type="button"
                        className={`${typeShowMode !== "CARD" ? "btn-primary" : "btn-white"
                          } btn px-2 py-1`}
                        onClick={handleChangeViewTable}
                      >
                        <FeatherIcon
                          icon="list"
                          className={`${typeShowMode !== "CARD"
                              ? "text-white"
                              : "text-primary"
                            } icon-dual`}
                        />
                      </button>

                      <button
                        type="button"
                        className={`${typeShowMode === "CARD" ? "btn-primary" : "btn-white"
                          } btn px-2 py-1`}
                        onClick={handleChangeViewTable}
                      >
                        <FeatherIcon
                          icon="grid"
                          className={`${typeShowMode === "CARD"
                              ? "text-white"
                              : "text-primary"
                            } icon-dual`}
                        />
                      </button>
                    </div>
                  </Col>
                </Row>
                <CardBody className="pt-3">
                  <div>
                    <TableGridContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={lstSticker?.list?.length ? lstSticker?.list : []}
                      customPageSize={querySticker.limit}
                      customPageIndex={querySticker.page - 1}
                      totalRecords={lstSticker?.meta?.total}
                      customPageCount={Math.ceil(
                        Number(lstSticker?.meta?.total) / Number(querySticker.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: querySticker.sort_by,
                        order_by: querySticker.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isLoading}
                      typeShowMode={typeShowMode}
                      setLinkImage={setLinkImage}
                      handleClickEdit={handleClickEdit}
                      isShowHeaderTableFixedTab={typeShowMode}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {isOpen && (
          <StickerForm
            id={itemToEdit?.id}
            triggerRefresh={triggerRefresh}
            stickerPackageId={stickerPackageId}
            onCloseClick={onCloseClick}
          />
        )}

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete Sticker")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={"IMAGE"}
          isOpen={!!linkImage}
          url={linkImage}
          onClose={() => onCloseImageClick()}
        />
      </div>
    </React.Fragment>
  );
};

export default StickerList;
